import { format } from 'date-fns'
import { paragraphAnimation, titleAnimation } from './animations'
import React, { FunctionComponent } from 'react'
import Title, { TitleSizes } from '../../atoms/title/Title'
import Colors from '../../../tokens/Colors'
import { motion } from 'framer-motion'
import { rem } from '../../../utils/rem'
import styled from 'styled-components'

export interface TextProps {
  animateDelay?: number
  animateIn?: boolean
  className?: string
  date?: Date
  lead?: string
  link?: {
    linkText: string
    url: string
  } | null
  size?: 'small' | 'medium'
  text: string[]
  title?: string
}

const Text: FunctionComponent<TextProps> = ({
  animateDelay = 0,
  animateIn = true,
  className,
  lead,
  link,
  text,
  date,
  title,
  size = 'medium',
}) => (
  <StyledText className={className}>
    {title && (
      <AnimationWrapper
        animate={animateIn ? ['opacityIn', 'transformIn'] : 'initial'}
        variants={titleAnimation(animateDelay)}
      >
        <StyledTitle
          size={size === 'small' ? TitleSizes.xSmall : TitleSizes.xLarge}
        >
          {title}
        </StyledTitle>
      </AnimationWrapper>
    )}
    {date && (
      <Date
        animate={animateIn ? ['opacityIn', 'transformIn'] : 'initial'}
        variants={paragraphAnimation(animateDelay)}
      >
        {`${format(date, 'MMM dd, yyyy - HH:mm')} CEST`}
      </Date>
    )}
    {lead && (
      <Lead
        animate={animateIn ? ['opacityIn', 'transformIn'] : 'initial'}
        dangerouslySetInnerHTML={{
          __html: lead,
        }}
        variants={paragraphAnimation(animateDelay)}
      />
    )}
    {text.map((textInstance) => (
      <Paragraph
        animate={animateIn ? ['opacityIn', 'transformIn'] : 'initial'}
        dangerouslySetInnerHTML={{
          __html: textInstance,
        }}
        key={textInstance}
        variants={paragraphAnimation(animateDelay)}
      />
    ))}
    {link && (
      <ExternalLink
        animate={animateIn ? ['opacityIn', 'transformIn'] : 'initial'}
        href={link.url}
        variants={paragraphAnimation(animateDelay)}
      >
        {link.linkText}
      </ExternalLink>
    )}
  </StyledText>
)

const AnimationWrapper = styled(motion.div)``

const StyledText = styled.div``

const StyledTitle = styled(Title)`
  margin: 0 0 ${rem(8)};
`

const Paragraph = styled(motion.p)`
  font-size: ${rem(15)};
  line-height: ${rem(23)};
  letter-spacing: -0.02em;
  margin: 0 0 ${rem(16)};

  &:last-of-type {
    margin: 0;
  }
`

const Lead = styled(Paragraph)`
  font-weight: 700;
`

const Date = styled(Paragraph)`
  text-transform: uppercase;
  margin-top: ${rem(8)};
  font-weight: 700;
  color: ${Colors.SHIP_GREY};
  font-size: ${rem(10)};
  line-height: ${rem(16)};
  letter-spacing: 0.06em;
`

const ExternalLink = styled(motion.a).attrs({
  rel: 'noreferrer noopener',
  target: '_blank',
})`
  display: inline-block;
  font-size: ${rem(15)};
  font-weight: 700;
  line-height: ${rem(23)};
  margin: ${rem(16)} 0 0;
  color: ${Colors.OSLO_GREY};
  transition: color 200ms ease-out;
  text-decoration: none;
  letter-spacing: -0.02em;
  outline: none;

  &:hover {
    transition: color 200ms ease-in;
    color: ${Colors.SHIP_GREY};
  }
`

export default Text
